"use client";

/**
 * Curated game sections for the Play page — two hand-picked, ordered rows
 * ("Daily Challenges" and "Play with coins") in a horizontal scroller, each an
 * artwork card reusing the /demos hub illustration (DemoModeArt). The order and
 * membership are owner-defined (see the slug arrays below); any mode not listed
 * is intentionally off the Play screen for now.
 *
 * Each row is ordered by the server (how many people play a game, how new it is, whether it is rising; see
 * playRanking.ts) and falls back to the hand-made order in playCatalog.ts. A game released in the last three
 * weeks wears a NEW ribbon and is listed under the New filter.
 *
 * A completed daily challenge shows a done state + an "unlocks in Hh Mm" timer and keeps its place. Auction and
 * Tic-Tac-Toe keep their own bespoke cards above these sections.
 */

import Image from "next/image";
import Link from "next/link";
import { trackPlayCardClicked } from "@/lib/analytics/game-events";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, Clock3, Play, RotateCcw, Search, User, Users, Wifi, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { DemoModeArt } from "@/features/demos/DemoModeArt";
import { CARD_WIDTH, CardScroller } from "@/features/play/CardScroller";
import { ALL_DEMO_MODES, type DemoModeCard, demoText } from "@/features/demos/demoModes";
import { useDailyChallenges, useResetDailyChallengeDev } from "@/lib/queries/dailyChallenges.queries";
import { queryKeys } from "@/lib/queries/queryKeys";
import type { DailyChallengeType } from "@/lib/domain/dailyChallenge";
import { useAuthStore } from "@/stores/auth.store";
import { useAuthPromptStore } from "@/stores/authPrompt.store";
import { useIsGuest } from "@/lib/auth/useIsGuest";
import { rememberPostAuthRedirect } from "@/lib/auth/postAuthRedirect";
import { findPublicGameForCard, guestCardHref } from "@/lib/seo/public-games";
import { trackGameCardClick } from "@/lib/analytics/public-games.analytics";
import { dailyChallengePlayPath } from "@/lib/domain/dailyChallengeSlugs";
import { useLocale } from "@/contexts/LocaleContext";
import { useRouter } from "next/navigation";
import { MissingXiModeModal } from "@/features/missing-xi/components/MissingXiModeModal";
import { StatSniperModeModal } from "@/features/aproximado/StatSniperModeModal";
import { ROOM_GAMES_ENABLED } from "@/lib/config";
import { storage, STORAGE_KEYS } from "@/utils/storage";
import { CoinIcon } from "@/features/store/components/CoinIcon";
import type { Locale } from "@/lib/i18n/messages";
import { isMiniGamesEnabled } from "@/lib/features/playModes";
import { DAILY_CHALLENGE_SLUGS, PLAY_WITH_COINS_SLUGS } from "@/features/play/playCatalog";
import { usePlayOrder } from "@/features/play/PlayOrderProvider";

/** Real in-app destinations for the modes that have shipped a page. */
const REAL_ROUTES: Record<string, string> = {
  minuto: "/minuto",
  "shared-player": "/ortak-futbolcu",
  "name-chain": "/son-harfle",
  "football-horserace": "/horserace",
  "football-codenames": "/codenames",
  "dato-falso-futbolero": "/dato-falso-futbolero",
  "conexiones-futboleras": "/conexiones-futboleras",
  "football-darts-501": "/football-darts-501",
  buscaminas: "/buscaminas",
  pistas: "/pistas",
  ultimo: "/ultimo",
  tenable: "/football-tenable",
  "mini-final-third": "/free-kicks",
  "mini-road-to-goal": "/road-to-goal",
  "mini-guess-the-goal": "/guess-the-goal",
  "mini-trivia-mines": "/trivia-mines",
  "mini-squad-spin": "/squad-spin",
};

/** Where a card links: the real daily route or game route. Only concept
 *  prototypes that have no shipped page fall back to their /demos preview.
 *  Guests get the same links; the click handler opens the sign-in dialog
 *  for anything that needs an account instead of detouring through demos. */
function hrefFor(mode: DemoModeCard): string {
  if (mode.dailyType) return dailyChallengePlayPath(mode.dailyType);
  return REAL_ROUTES[mode.slug] ?? `/demos/${mode.slug}?from=/play`;
}

/** True when the destination is a shipped game (which also needs an account). */
function hasRealRoute(mode: DemoModeCard): boolean {
  return Boolean(mode.dailyType) || mode.slug in REAL_ROUTES;
}

/** Live matchmaking against a real opponent — the "online" badge (orange). */
const LIVE_ONLINE_SLUGS = new Set(["match", "auction", "mini-football-grid", "weekend-league"]);

/** Format badge: solo (yellow) / multiplayer with friends (green) / online (orange). */
type PlayFormat = "solo" | "multiplayer" | "online";
const FORMAT_LABEL: Record<PlayFormat, Record<string, string>> = {
  solo: { en: "Solo", ka: "სოლო", es: "Solo", tr: "Tek kişilik", ru: "Одиночная", pt: "Solo", de: "Solo" },
  multiplayer: { en: "Multiplayer", ka: "მულტიპლეიერი", es: "Multijugador", tr: "Çok oyunculu", ru: "Мультиплеер", pt: "Multijogador", de: "Mehrspieler" },
  online: { en: "Online 1v1", ka: "ონლაინ 1v1", es: "Online 1v1", tr: "Online 1v1", ru: "Онлайн 1 на 1", pt: "Online 1v1", de: "Online 1v1" },
};
const FORMAT_STYLE: Record<PlayFormat, string> = {
  solo: "bg-brand-yellow text-black",
  multiplayer: "bg-brand-green text-white",
  online: "bg-brand-orange text-white",
};
function formatOf(slug: string): PlayFormat {
  if (LIVE_ONLINE_SLUGS.has(slug)) return "online";
  if (ONLINE_SLUGS.has(slug)) return "multiplayer";
  return "solo";
}

/** Games playable with other people (live or party-style); everything else is solo. */
/** Second format a mode also ships in (Missing XI: solo now, multiplayer coming). */
const EXTRA_FORMATS: Record<string, PlayFormat[]> = { "lab-missing-xi": ["multiplayer"], tenable: ["multiplayer"] };

const ONLINE_SLUGS = new Set([
  "lab-own-goal",
  "lab-say-it-with-memes",
  "lab-top-10-knockout",
  "lab-ball-knowledge",
  "lab-bingo-battle",
  "lab-connections-race",
  "lab-stat-501",
  "mini-golden-goal",
  "mini-career-race",
  "mini-quiz-board",
  "mini-last-one-standing",
  "mini-football-grid",
]);

const MODE_BY_SLUG = new Map(ALL_DEMO_MODES.map((mode) => [mode.slug, mode]));
const resolveModes = (slugs: string[]): DemoModeCard[] =>
  slugs
    .map((slug) => MODE_BY_SLUG.get(slug))
    .filter((mode): mode is DemoModeCard => Boolean(mode));

// Prototype modes (no shipped route yet) stay listed — they are being turned
// into real games — and open their /demos preview until their route lands.
const DAILY_CHALLENGE_MODES = resolveModes(DAILY_CHALLENGE_SLUGS);
const PLAY_WITH_COINS_MODES = isMiniGamesEnabled ? resolveModes(PLAY_WITH_COINS_SLUGS) : [];

/** The row in the server's order; a game the server did not list keeps its hand-made place at the end. */
function inOrder(modes: DemoModeCard[], order: string[] | null): DemoModeCard[] {
  if (!order) return modes;
  const place = new Map(order.map((slug, index) => [slug, index]));
  return [...modes].sort((a, b) => (place.get(a.slug) ?? order.length) - (place.get(b.slug) ?? order.length));
}

const STAT_SNIPER_ROOMS = ROOM_GAMES_ENABLED.includes("aproximado");
const MODAL_SLUGS = new Set(["lab-missing-xi", ...(STAT_SNIPER_ROOMS ? ["daily-statSniper"] : [])]);
// Their dialog also offers a way to play once today's daily is done (Stat Sniper: friends), so the done tile opens it.
const MODAL_WHEN_COMPLETED = new Set(STAT_SNIPER_ROOMS ? ["daily-statSniper"] : []);

/** ms until the next 00:00 UTC — the daily-challenge reset boundary. */
function msUntilUtcReset(): number {
  const now = new Date();
  const next = new Date(now);
  next.setUTCHours(24, 0, 0, 0);
  return Math.max(0, next.getTime() - now.getTime());
}

function formatCountdown(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

/** Coarse "7h 23m" label until the daily reset, refreshed every 30s (drives the
 *  per-card "unlocks in" text; minute precision is plenty). Empty until mount. */
function useUnlockInLabel(): string {
  const [ms, setMs] = useState<number | null>(null);
  useEffect(() => {
    const tick = () => setMs(msUntilUtcReset());
    tick();
    const id = window.setInterval(tick, 30_000);
    return () => window.clearInterval(id);
  }, []);
  if (ms == null) return "";
  const totalMinutes = Math.max(0, Math.floor(ms / 60_000));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/** Live HH:MM:SS to the daily reset, next to the Daily Challenges header. Own
 *  state/interval so its per-second re-render never touches the card list;
 *  renders nothing until mount (avoids a hydration mismatch). */
function DailyResetTimer() {
  const [text, setText] = useState<string | null>(null);
  useEffect(() => {
    const tick = () => setText(formatCountdown(msUntilUtcReset()));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);
  if (!text) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-white/[0.08] px-2.5 py-1 font-poppins text-[10px] font-bold uppercase leading-none tracking-wide tabular-nums text-white/80 md:text-xs">
      <Clock3 className="size-3 text-brand-yellow" />
      {text}
    </span>
  );
}

/** Admin-only "play it again" control, restored from the retired daily hub
 *  (PR #488 replaced that page with a redirect and the button went with it).
 *  Clears today's completion so the challenge can be replayed immediately. */
function DailyDevResetButton({ challengeType, title }: { challengeType: DailyChallengeType; title: string }) {
  const { t } = useLocale();
  const queryClient = useQueryClient();
  const resetMutation = useResetDailyChallengeDev(challengeType);

  const handleReset = async (event: React.MouseEvent<HTMLButtonElement>) => {
    // The card is a Link; never navigate when the reset is tapped.
    event.preventDefault();
    event.stopPropagation();
    try {
      await resetMutation.mutateAsync();
      await queryClient.invalidateQueries({ queryKey: queryKeys.dailyChallenges.all });
      toast.success(t("dailyGames.hubResetSuccess", { title }));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("dailyGames.hubResetError"));
    }
  };

  return (
    <button
      type="button"
      onClick={handleReset}
      disabled={resetMutation.isPending}
      className="absolute right-1.5 top-1.5 z-10 inline-flex items-center gap-1 rounded-md bg-black/60 px-1.5 py-0.5 font-poppins text-[8px] font-black uppercase tracking-wide text-white transition-colors hover:bg-black/80 disabled:opacity-50 md:right-2 md:top-2 md:rounded-lg md:px-2 md:py-1 md:text-[10px]"
    >
      <RotateCcw className="size-2.5 md:size-3" />
      {resetMutation.isPending ? "…" : t("dailyGames.hubResetButton")}
    </button>
  );
}

/** The diagonal NEW band across a card's picture. A coin game's COINS pill has the right corner, so its band crosses the left one, below the format pill. */
function NewRibbon({ label, beside }: { label: string; beside: "left" | "right" }) {
  return (
    <span
      data-play-new
      className={`pointer-events-none absolute z-[6] w-32 bg-brand-green py-1 text-center font-poppins text-[9px] font-black uppercase tracking-[0.2em] text-white shadow-md shadow-black/40 md:text-[11px] ${
        beside === "left" ? "-left-10 top-12 -rotate-45 md:-left-9 md:top-14" : "-right-10 top-3 rotate-45 md:-right-9 md:top-4"
      }`}
    >
      {label}
    </span>
  );
}

function GameCard({
  mode,
  index,
  completed = false,
  unlockLabel = "",
  showDevReset = false,
  onOpenMode,
  isNew = false,
}: {
  mode: DemoModeCard;
  index: number;
  /** Released in the last three weeks: the card wears the NEW ribbon. */
  isNew?: boolean;
  completed?: boolean;
  unlockLabel?: string;
  showDevReset?: boolean;
  /** Modes with their own entry dialog (Missing XI) open it instead of navigating. */
  onOpenMode?: (slug: string) => void;
}) {
  const { t, locale } = useLocale();
  const isGuest = useIsGuest();
  const openAuthPrompt = useAuthPromptStore((state) => state.open);
  const devReset = showDevReset && mode.dailyType
    ? <DailyDevResetButton challengeType={mode.dailyType as DailyChallengeType} title={demoText(mode.title, locale)} />
    : null;
  // Signed-out visitors go to the public page (or owning quiz) for the mode.
  const publicGame = findPublicGameForCard(mode);
  const guestHref = isGuest ? guestCardHref(mode, locale) : null;
  const title = demoText(mode.title, locale);
  const description = demoText(mode.description, locale);
  const format = formatOf(mode.slug);

  // Completed daily: a done state + reset timer, not tappable until it unlocks.
  if (completed) {
    return (
      <div
        className={`relative flex ${CARD_WIDTH} flex-col overflow-hidden rounded-xl bg-brand-blue/50`}
        aria-label={`${title} — ${t("play.completed")}`}
      >
        {devReset}
        <div className="relative aspect-video w-full overflow-hidden">
          <DemoModeArt slug={mode.slug} className="size-full opacity-25" />
          {isNew && <NewRibbon label={t("play.newBadge")} beside="right" />}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/45 text-center">
            <CheckCircle2 className="size-6 text-brand-green-light md:size-7" />
            <span className="font-poppins text-[9px] font-bold uppercase tracking-wide text-white md:text-[11px]">
              {t("play.completed")}
            </span>
          </div>
        </div>
        <div className="flex flex-1 flex-col p-2.5 md:p-3">
          <h3 className="line-clamp-2 font-poppins text-[12px] font-semibold uppercase text-white/70 md:text-[15px]">
            {title}
          </h3>
          {unlockLabel && (
            <p className="mt-2 flex items-center gap-1 font-poppins text-[9px] uppercase tracking-wide text-brand-yellow md:text-[10px]">
              <Clock3 className="size-2.5" /> {t("play.unlocksIn", { time: unlockLabel })}
            </p>
          )}
        </div>
        {onOpenMode && !isGuest && MODAL_WHEN_COMPLETED.has(mode.slug) && (
          <button type="button" className="absolute inset-0 z-[5] rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/70"
            aria-label={`${title} — ${t("play.completed")}`} aria-haspopup="dialog"
            onClick={() => { trackPlayCardClicked({ slug: mode.slug, group: "daily", destination: "modal" }); onOpenMode(mode.slug); }} />
        )}
      </div>
    );
  }

  return (
    <div className={`relative flex ${CARD_WIDTH} shrink-0`}>
    {devReset}
    <Link
      href={guestHref ?? hrefFor(mode)}
      onClick={(event) => {
        const group = PLAY_WITH_COINS_SLUGS.includes(mode.slug) ? "coins" : DAILY_CHALLENGE_SLUGS.includes(mode.slug) ? "daily" : "other";
        if (isGuest) {
          // Public page when the mode has one; otherwise the sign-in dialog
          // (never a /demos preview, which production does not serve).
          if (guestHref && publicGame) {
            trackPlayCardClicked({ slug: mode.slug, group, destination: "public_page" });
            trackGameCardClick({ modeId: publicGame.modeId, surface: "hub", group: publicGame.group, destination: publicGame.destination.kind });
            return;
          }
          event.preventDefault();
          trackPlayCardClicked({ slug: mode.slug, group, destination: "auth" });
          trackGameCardClick({ modeId: publicGame?.modeId ?? mode.dailyType ?? mode.slug, surface: "hub", group: publicGame?.group ?? group, destination: "auth" });
          rememberPostAuthRedirect(hasRealRoute(mode) ? hrefFor(mode) : null);
          openAuthPrompt();
          return;
        }
        if (onOpenMode) {
          event.preventDefault();
          trackPlayCardClicked({ slug: mode.slug, group, destination: "modal" });
          onOpenMode(mode.slug);
          return;
        }
        trackPlayCardClicked({ slug: mode.slug, group, destination: hasRealRoute(mode) ? "route" : "demo" });
      }}
      className={`group relative flex w-full animate-in fade-in slide-in-from-bottom-2 flex-col overflow-hidden rounded-xl bg-brand-blue duration-300 transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60`}
      style={{ animationDelay: `${Math.min(index * 30, 420)}ms`, animationFillMode: "backwards" }}
    >
      <div className="relative aspect-video w-full overflow-hidden">
        <DemoModeArt
          slug={mode.slug}
          className="size-full transition-transform duration-300 group-hover:scale-[1.05]"
        />

        {/* Format badges — same pill as NEW, colour-coded so the three formats
            read at a glance: yellow solo, green multiplayer, orange online.
            Modes that ship in more than one format (Missing XI) show each. */}
        {PLAY_WITH_COINS_SLUGS.includes(mode.slug) && (
          <span className="absolute right-2 top-2 inline-flex size-7 items-center justify-center rounded-full bg-brand-yellow shadow-md shadow-black/30 md:size-auto md:gap-1 md:px-2.5 md:py-1" title={t("play.coinsBadge")} aria-label={t("play.coinsBadge")}>
            <CoinIcon size={16} />
            <span className="hidden font-poppins text-[11px] font-bold uppercase leading-none tracking-wide text-black md:inline">{t("play.coinsBadge")}</span>
          </span>
        )}
        <span className="absolute left-2 top-2 flex items-center gap-1">
          {[format, ...(EXTRA_FORMATS[mode.slug] ?? [])].map((entry) => (
            <span
              key={entry}
              title={FORMAT_LABEL[entry][locale] ?? FORMAT_LABEL[entry].en}
              aria-label={FORMAT_LABEL[entry][locale] ?? FORMAT_LABEL[entry].en}
              className={`inline-flex items-center justify-center gap-1 rounded-full font-poppins text-[11px] font-bold uppercase leading-none tracking-wide shadow-md shadow-black/30 size-7 md:size-auto md:px-2.5 md:py-1 ${FORMAT_STYLE[entry]}`}
            >
              {entry === "online" ? <Wifi className="size-3.5 md:size-3" /> : entry === "multiplayer" ? <Users className="size-3.5 md:size-3" /> : <User className="size-3.5 md:size-3" />}
              {/* Phones: icon only — the text pill swamped the small cards. */}
              <span className="hidden md:inline">{FORMAT_LABEL[entry][locale] ?? FORMAT_LABEL[entry].en}</span>
            </span>
          ))}
        </span>

        {isNew && <NewRibbon label={t("play.newBadge")} beside={PLAY_WITH_COINS_SLUGS.includes(mode.slug) ? "left" : "right"} />}

        <div className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 transition-opacity group-hover:opacity-100">
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3.5 py-1.5 font-poppins text-[11px] font-bold uppercase text-black md:text-xs">
            <Play className="size-3 fill-current" />
            {locale === "ka" ? "თამაში" : locale === "es" ? "Jugar" : locale === "tr" ? "Oyna" : locale === "ru" ? "Играть" : locale === "pt" ? "Jogue" : locale === "de" ? "Spielen" : "Play"}
          </span>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-2.5 md:p-3">
        <h3 className="line-clamp-2 font-poppins text-[12px] font-semibold uppercase text-white md:text-[15px]">
          {title}
        </h3>
        <p className="mt-1 line-clamp-2 font-poppins text-[10px] leading-snug text-white/70 md:text-[12px]">
          {description}
        </p>
        <p className="mt-2 flex items-center gap-1 font-poppins text-[9px] text-white/55 md:text-[10px]">
          <Clock3 className="size-2.5" /> {locale === "ka" ? "1–2 წთ" : locale === "tr" ? "1–2 dk" : locale === "ru" ? "1–2 мин" : locale === "pt" ? "1–2 min" : locale === "de" ? "1–2 Min." : "1–2 min"}
        </p>
      </div>
    </Link>
    </div>
  );
}

function GameSection({
  title,
  hint,
  modes,
  headerRight,
  isCompleted,
  unlockLabel = "",
  showDevReset = false,
  onOpenMode,
  isNew,
}: {
  isNew?: (mode: DemoModeCard) => boolean;
  title: string;
  /** Muted coin-flavoured hint next to the title ("Earn coins", …). */
  hint?: string;
  modes: DemoModeCard[];
  headerRight?: React.ReactNode;
  isCompleted?: (mode: DemoModeCard) => boolean;
  unlockLabel?: string;
  showDevReset?: boolean;
  onOpenMode?: (slug: string) => void;
}) {
  if (modes.length === 0) return null;
  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3 md:mb-4">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-0.5">
          <h2 className="font-poppins text-[16px] font-semibold uppercase leading-tight text-white md:text-[24px]">
            {title}
          </h2>
          {hint && (
            <span className="inline-flex items-center gap-1 font-poppins text-[10px] font-bold uppercase tracking-wide text-white/50 md:text-xs">
              <Image
                src="/assets/coin-1.png?v=2"
                alt=""
                width={16}
                height={16}
                className="size-3.5 object-contain md:size-4"
              />
              {hint}
            </span>
          )}
        </div>
        {headerRight && <div className="flex shrink-0 items-center gap-2">{headerRight}</div>}
      </div>
      {/* Horizontal scroller — bleeds to the screen edges (-mx-4 px-4) so a card
          peeks past the viewport and invites the swipe; 3 cards fit per row. */}
      <CardScroller>
        {modes.map((mode, index) => (
          <GameCard
            key={mode.slug}
            mode={mode}
            index={index}
            completed={isCompleted?.(mode) ?? false}
            isNew={isNew?.(mode) ?? false}
            unlockLabel={unlockLabel}
            showDevReset={showDevReset}
            onOpenMode={MODAL_SLUGS.has(mode.slug) ? onOpenMode : undefined}
          />
        ))}
      </CardScroller>
    </div>
  );
}


// The finder lists the daily and coin games only, so a solo/multiplayer/online split would carry no information here.
type FinderFilter = "all" | "new" | "daily" | "coins";
const FINDER_FILTERS: Array<{ id: FinderFilter } & Record<Locale, string>> = [
  { id: "all", en: "All", ka: "ყველა", es: "Todos", tr: "Tümü", ru: "Все", pt: "Todos", de: "Alle" },
  { id: "new", en: "New", ka: "ახალი", es: "Nuevos", tr: "Yeni", ru: "Новые", pt: "Novos", de: "Neu" },
  { id: "daily", en: "Daily", ka: "დღიური", es: "Diarios", tr: "Günlük", ru: "Ежедневно", pt: "Diários", de: "Täglich" },
  { id: "coins", en: "Coins", ka: "მონეტები", es: "Monedas", tr: "Jeton", ru: "Монеты", pt: "Moedas", de: "Münzen" },
];

function matchesFilter(section: "daily" | "coins", filter: FinderFilter, isNew: boolean): boolean {
  if (filter === "all") return true;
  if (filter === "new") return isNew;
  return section === filter;
}

// Accent- and case-insensitive so "Gol" finds "Golü" and "sut" finds "Şut" (Turkish ı folds to i).
function searchKey(value: string): string {
  return value.trim().toLowerCase().replace(/ı/g, "i").replace(/ß/g, "ss").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function matchesQuery(mode: DemoModeCard, query: string): boolean {
  const q = searchKey(query);
  if (!q) return true;
  return [mode.title, mode.description].flatMap((text) => [text.en, text.ka, text.es ?? "", text.tr ?? "", text.ru ?? "", text.pt ?? "", text.de ?? ""]).concat(mode.slug)
    .some((text) => searchKey(text).includes(q));
}

/** Search icon that slides open into a field, plus the filter pills. */
function GamesFinder({
  query,
  onQuery,
  filter,
  onFilter,
  counts,
  hasNewGames,
}: {
  query: string;
  onQuery: (value: string) => void;
  filter: FinderFilter;
  onFilter: (value: FinderFilter) => void;
  counts: Record<FinderFilter, number>;
  /** Whether any game is new at all: the New pill does not come and go with what is typed in the search. */
  hasNewGames: boolean;
}) {
  const { locale } = useLocale();
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const label = locale === "ka" ? "მოძებნე თამაში" : locale === "es" ? "Buscar juegos" : locale === "tr" ? "Oyun ara" : locale === "ru" ? "Поиск игр" : locale === "pt" ? "Buscar jogos" : locale === "de" ? "Spiele suchen" : "Search games";
  const close = () => {
    onQuery("");
    setOpen(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center">
        <button
          type="button"
          onClick={() => {
            if (open) close();
            else {
              setOpen(true);
              requestAnimationFrame(() => inputRef.current?.focus());
            }
          }}
          aria-label={label}
          aria-expanded={open}
          className={`relative z-10 flex size-9 items-center justify-center rounded-full transition-colors ${
            open ? "bg-brand-yellow text-black" : "bg-white/[0.07] text-white/70 hover:bg-white/[0.12]"
          }`}
        >
          {open ? <X className="size-4" /> : <Search className="size-4" />}
        </button>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="finder-field"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: "min(260px, 60vw)", opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ type: "spring", stiffness: 420, damping: 36 }}
              className="overflow-hidden"
            >
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(event) => onQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") close();
                }}
                placeholder={label}
                aria-label={label}
                className="font-poppins ml-2 h-9 w-full rounded-full border-none bg-brand-blue px-4 text-[13px] font-semibold uppercase tracking-[0.06em] text-white outline-none placeholder:text-white/55 focus:outline-none"
                style={{ boxShadow: "0 1.76px 6.334px 1.32px rgba(22, 69, 255, 0.25)" }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {/* The New pill is there only while a game is new (or while it is the chosen filter). */}
      {FINDER_FILTERS.filter((entry) => entry.id !== "new" || hasNewGames || filter === "new").map((entry) => (
        <button
          key={entry.id}
          type="button"
          onClick={() => onFilter(entry.id)}
          aria-pressed={filter === entry.id}
          data-play-filter={entry.id}
          className={`font-poppins inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide transition-colors md:text-xs ${
            filter === entry.id ? "bg-brand-yellow text-black" : "bg-white/[0.07] text-white/60 hover:bg-white/[0.12]"
          }`}
        >
          {entry[locale] ?? entry.en}
          <span className={filter === entry.id ? "text-black/50" : "text-white/35"}>{counts[entry.id]}</span>
        </button>
      ))}
    </div>
  );
}

/** Named AllGamesGrid for backwards-compat with its single import; renders the
 *  two curated horizontal-scroll sections rather than a searchable grid. */
export function AllGamesGrid() {
  const { t, locale } = useLocale();
  const isGuest = useIsGuest();
  // Admin-only replay control (hidden for everyone else, including in prod).
  const canUseDevReset = useAuthStore((state) => state.user?.role) === "admin";
  const { data: dailyChallenges = [] } = useDailyChallenges();
  const unlockLabel = useUnlockInLabel();

  const completedByType = new Map(
    dailyChallenges.map((challenge) => [challenge.challengeType, challenge.completedToday]),
  );
  // A guest never has a completed daily (an account's cached list can outlive a logout).
  const isDailyCompleted = (mode: DemoModeCard): boolean =>
    !isGuest && mode.dailyType ? (completedByType.get(mode.dailyType) ?? false) : false;

  // Completion is over the cards actually shown in this row, not the backend's
  // active-challenge count (which is smaller — e.g. inactive/hidden types).
  // Only cards with a daily type report completion (Minuto, Buscaminas… don't),
  // so the rest stay out of the total or it could never be reached.
  // Completed cards keep their curated position (owner call — no re-sort).
  const dailyTotal = DAILY_CHALLENGE_MODES.filter((mode) => mode.dailyType).length;
  const dailyDone = DAILY_CHALLENGE_MODES.filter(isDailyCompleted).length;

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<FinderFilter>("all");
  const router = useRouter();
  const [missingXiOpen, setMissingXiOpen] = useState(false);
  const [statSniperOpen, setStatSniperOpen] = useState(false);
  const openMode = useCallback((slug: string) => {
    if (slug === "lab-missing-xi") setMissingXiOpen(true);
    if (slug === "daily-statSniper") setStatSniperOpen(true);
  }, []);
  // Decided on the server (see playOrder.server.ts): the rows arrive already in order and nothing moves afterwards.
  const playOrder = usePlayOrder();
  const isNew = useCallback((mode: DemoModeCard) => playOrder.newSlugs.includes(mode.slug), [playOrder.newSlugs]);
  const dailyModes = useMemo(() => inOrder(DAILY_CHALLENGE_MODES, playOrder.daily), [playOrder.daily]);
  const coinModes = useMemo(() => inOrder(PLAY_WITH_COINS_MODES, playOrder.coins), [playOrder.coins]);
  const visibleDaily = useMemo(
    () => dailyModes.filter((mode) => matchesFilter("daily", filter, isNew(mode)) && matchesQuery(mode, query)),
    [dailyModes, filter, isNew, query],
  );
  const visibleCoins = useMemo(
    () => coinModes.filter((mode) => matchesFilter("coins", filter, isNew(mode)) && matchesQuery(mode, query)),
    [coinModes, filter, isNew, query],
  );
  const counts = useMemo(() => {
    const all = [...DAILY_CHALLENGE_MODES.map((mode) => ({ mode, section: "daily" as const })), ...PLAY_WITH_COINS_MODES.map((mode) => ({ mode, section: "coins" as const }))]
      .filter(({ mode }) => matchesQuery(mode, query));
    const count = (id: FinderFilter) => all.filter(({ mode, section }) => matchesFilter(section, id, isNew(mode))).length;
    return { all: all.length, new: count("new"), daily: count("daily"), coins: count("coins") };
  }, [isNew, query]);
  const nothing = visibleDaily.length === 0 && visibleCoins.length === 0;

  return (
    <div className="space-y-6 md:space-y-8">
      <GamesFinder query={query} onQuery={setQuery} filter={filter} onFilter={setFilter} counts={counts}
        hasNewGames={DAILY_CHALLENGE_MODES.some(isNew) || PLAY_WITH_COINS_MODES.some(isNew)} />
      <MissingXiModeModal
        isOpen={missingXiOpen}
        onOpenChange={setMissingXiOpen}
        onPlaySolo={() => {
          setMissingXiOpen(false);
          // Solo runs on the prototype until the real route ships.
          router.push(dailyChallengePlayPath("missingXi"));
        }}
      />
      {STAT_SNIPER_ROOMS && !isGuest && (
        <StatSniperModeModal
          isOpen={statSniperOpen}
          onOpenChange={setStatSniperOpen}
          completed={completedByType.get("statSniper") ?? false}
          unlockLabel={unlockLabel}
          onPlaySolo={() => { setStatSniperOpen(false); router.push(dailyChallengePlayPath("statSniper")); }}
          onPlayWithFriends={() => {
            setStatSniperOpen(false);
            // App routes read the stored language: keep the room in the language the player is reading.
            storage.set(STORAGE_KEYS.LOCALE, locale);
            router.push("/friend/room/new?room=aproximado");
          }}
        />
      )}
      {nothing && (
        <p className="font-poppins text-sm text-white/55">{t("play.finderNoMatches")}</p>
      )}
      {visibleDaily.length > 0 && (
      <GameSection
        title={t("play.sectionDailyChallenges")}
        hint={t("play.sectionDailyChallengesHint")}
        modes={visibleDaily}
        onOpenMode={openMode}
        isNew={isNew}
        isCompleted={isDailyCompleted}
        unlockLabel={unlockLabel}
        showDevReset={canUseDevReset}
        headerRight={
          <>
            <DailyResetTimer />
            {/* Completion is an account concept — guests just see the timer. */}
            {!isGuest && dailyTotal > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/[0.08] px-2.5 py-1 font-poppins text-[10px] font-bold uppercase leading-none tracking-wide tabular-nums text-white/80 md:text-xs">
                <CheckCircle2 className="size-3 text-brand-green-light" />
                {dailyDone}/{dailyTotal}
              </span>
            )}
          </>
        }
      />
      )}
      {visibleCoins.length > 0 && (
      <GameSection
        title={t("play.sectionPlayCoins")}
        hint={t("play.sectionPlayCoinsHint")}
        modes={visibleCoins}
        onOpenMode={openMode}
        isNew={isNew}
      />
      )}
    </div>
  );
}
